import React, { useEffect, useState } from 'react';
import { audit } from '../lib/auditApi';
import { PageLayout } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Loader2, RefreshCw, ShieldCheck, AlertTriangle, FileSearch, Layers, Activity, Network } from 'lucide-react';
import { toast } from 'sonner';
import ModuleMap from '../components/audit/ModuleMap';
import StyleViolationsPanel from '../components/audit/StyleViolationsPanel';

const SEVERITY_CLS = {
  critical: 'bg-red-600 text-white',
  high: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300 border-red-300',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border-amber-300',
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300',
};

function FindingsTable({ findings, onUpdated }) {
  const [updating, setUpdating] = useState(null);
  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      await audit.patchFinding(id, { status });
      toast.success('Updated');
      onUpdated?.();
    } catch (e) {
      toast.error('Update failed: ' + (e.detail || e.message));
    } finally {
      setUpdating(null);
    }
  };
  if (!findings.length) return <p className="text-sm text-muted-foreground py-4">No findings.</p>;
  return (
    <div className="space-y-2">
      {findings.map((f) => (
        <Card key={f.finding_id} data-testid={`audit-finding-${f.finding_id}`}>
          <CardContent className="pt-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge className={SEVERITY_CLS[f.severity] || ''} variant="outline">{f.severity}</Badge>
                  <Badge variant="outline">{f.rule_code}</Badge>
                  <Badge variant="outline">{f.area}</Badge>
                  <Badge variant="outline" className="text-xs">{f.recommended_action}</Badge>
                  {f.suggested_phase && <Badge variant="outline" className="text-xs">→ {f.suggested_phase}</Badge>}
                  <Badge variant={f.status === 'open' ? 'default' : 'secondary'} className="ml-auto">{f.status}</Badge>
                </div>
                <h3 className="font-semibold text-sm">{f.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">{f.description}</p>
              </div>
            </div>
            {(f.file_refs || []).length > 0 && (
              <div className="border-t pt-2 mt-1">
                <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                  {f.file_refs.length} file ref{f.file_refs.length !== 1 ? 's' : ''}
                </div>
                <div className="space-y-1 max-h-48 overflow-auto">
                  {f.file_refs.slice(0, 8).map((r, i) => (
                    <div key={i} className="text-xs font-mono bg-muted/40 rounded p-1.5">
                      <span className="text-violet-600 dark:text-violet-400">{r.path}</span>
                      {r.line_start && <span className="text-muted-foreground">:{r.line_start}</span>}
                      {r.snippet && <div className="text-foreground mt-1 break-all">{r.snippet}</div>}
                    </div>
                  ))}
                  {f.file_refs.length > 8 && (
                    <div className="text-[11px] text-muted-foreground">+{f.file_refs.length - 8} more</div>
                  )}
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" disabled={updating === f.finding_id || f.status === 'accepted'}
                onClick={() => updateStatus(f.finding_id, 'accepted')}
                data-testid={`finding-accept-${f.finding_id}`}>
                Accept
              </Button>
              <Button size="sm" variant="outline" disabled={updating === f.finding_id || f.status === 'fixed'}
                onClick={() => updateStatus(f.finding_id, 'fixed')}>
                Mark fixed
              </Button>
              <Button size="sm" variant="ghost" disabled={updating === f.finding_id || f.status === 'wont_fix'}
                onClick={() => updateStatus(f.finding_id, 'wont_fix')}>
                Won't fix
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function AdminAuditPage() {
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState(null);
  const [findings, setFindings] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [filter, setFilter] = useState({ severity: 'all', area: 'all', status: 'open' });
  const [moduleMapData, setModuleMapData] = useState(null);

  useEffect(() => {
    fetch('/api/testing-lab/super-admin/me', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setIsAdmin(!!d.is_super_admin))
      .catch(() => setIsAdmin(false));
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const f = {};
      if (filter.severity !== 'all') f.severity = filter.severity;
      if (filter.area !== 'all') f.area = filter.area;
      if (filter.status !== 'all') f.status = filter.status;
      const [s, fr, inv, mm] = await Promise.all([
        audit.summary(),
        audit.findings(f),
        audit.inventory({ limit: 200 }),
        audit.moduleMap(),
      ]);
      setSummary(s);
      setFindings(fr.items || []);
      setInventory(inv.items || []);
      setModuleMapData(mm);
    } catch (e) {
      toast.error('Could not load audit: ' + (e.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) reload(); }, [isAdmin, filter]);  // eslint-disable-line

  const runAudit = async () => {
    setRunning(true);
    try {
      const r = await audit.run();
      toast.success(`Audit complete · ${r.inventory_count} items, ${r.findings_count} findings`);
      reload();
    } catch (e) {
      toast.error('Run failed: ' + (e.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  if (isAdmin === null) {
    return <PageLayout><div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div></PageLayout>;
  }
  if (!isAdmin) {
    return (
      <PageLayout>
        <div className="max-w-md mx-auto p-12 text-center">
          <ShieldCheck className="w-12 h-12 mx-auto text-slate-400" />
          <h1 className="text-2xl font-bold mt-4">Super Admin only</h1>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div data-testid="admin-audit-page" className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileSearch className="w-7 h-7 text-violet-500" />
            <div>
              <h1 className="text-3xl font-bold">Architecture Audit</h1>
              <p className="text-slate-500 text-sm">
                Inventory + legacy findings (Phase 2.1, Audit-First Restructure PRD §6).
              </p>
            </div>
          </div>
          <Button onClick={runAudit} disabled={running} className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="audit-run-btn">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Run audit
          </Button>
        </div>

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card data-testid="audit-stat-inventory">
              <CardContent className="pt-4">
                <Layers className="w-4 h-4 text-violet-500 mb-1" />
                <div className="text-2xl font-bold">{summary.inventory_count}</div>
                <div className="text-xs text-muted-foreground">inventory items</div>
              </CardContent>
            </Card>
            <Card data-testid="audit-stat-open">
              <CardContent className="pt-4">
                <AlertTriangle className="w-4 h-4 text-amber-500 mb-1" />
                <div className="text-2xl font-bold">{summary.open_findings}</div>
                <div className="text-xs text-muted-foreground">open findings</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-1">
                  {Object.entries(summary.by_severity || {}).map(([sev, n]) => (
                    n > 0 && <Badge key={sev} className={SEVERITY_CLS[sev]} variant="outline">{n} {sev}</Badge>
                  ))}
                </div>
                <div className="text-xs text-muted-foreground mt-2">by severity</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <Activity className="w-4 h-4 text-emerald-500 mb-1" />
                <div className="text-2xl font-bold">{summary.latest_run?.duration_ms || 0}ms</div>
                <div className="text-xs text-muted-foreground">last run duration</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="findings">
          <TabsList>
            <TabsTrigger value="findings" data-testid="audit-tab-findings">Findings</TabsTrigger>
            <TabsTrigger value="inventory" data-testid="audit-tab-inventory">Inventory</TabsTrigger>
            <TabsTrigger value="module-map" data-testid="audit-tab-module-map">Module Map</TabsTrigger>
            <TabsTrigger value="style-violations" data-testid="audit-tab-style-violations">Style Violations</TabsTrigger>
          </TabsList>

          <TabsContent value="findings" className="space-y-3 mt-3">
            <div className="flex flex-wrap gap-2 items-center">
              <Select value={filter.severity} onValueChange={(v) => setFilter({ ...filter, severity: v })}>
                <SelectTrigger className="w-32" data-testid="filter-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filter.area} onValueChange={(v) => setFilter({ ...filter, area: v })}>
                <SelectTrigger className="w-44" data-testid="filter-area"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All areas</SelectItem>
                  <SelectItem value="identity">Identity</SelectItem>
                  <SelectItem value="permissions">Permissions</SelectItem>
                  <SelectItem value="tasks">Tasks</SelectItem>
                  <SelectItem value="design_system">Design system</SelectItem>
                  <SelectItem value="ai">AI</SelectItem>
                  <SelectItem value="data_model">Data model</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
                <SelectTrigger className="w-32" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="fixed">Fixed</SelectItem>
                  <SelectItem value="wont_fix">Won't fix</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="outline">{findings.length} findings</Badge>
            </div>
            {loading ? <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
              : <FindingsTable findings={findings} onUpdated={reload} />}
          </TabsContent>

          <TabsContent value="inventory" className="space-y-2 mt-3">
            <div className="text-xs text-muted-foreground">{inventory.length} items shown (limited to 200)</div>
            <div className="border rounded">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr><th className="text-left p-2">Type</th><th className="text-left p-2">Name</th><th className="text-left p-2">Owner</th><th className="text-left p-2">Status</th></tr>
                </thead>
                <tbody>
                  {inventory.map((i) => (
                    <tr key={i.item_id} className="border-t">
                      <td className="p-2"><Badge variant="outline" className="text-[10px]">{i.item_type}</Badge></td>
                      <td className="p-2 font-mono text-[11px]">{i.name}</td>
                      <td className="p-2"><Badge variant="outline" className="text-[10px]">{i.current_owner_area}</Badge></td>
                      <td className="p-2"><Badge variant={i.status === 'current' ? 'outline' : 'secondary'} className="text-[10px]">{i.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="module-map" className="mt-3" data-testid="audit-tab-module-map-panel">
            {moduleMapData ? (
              <ModuleMap data={moduleMapData} />
            ) : (
              <div className="py-12 flex flex-col items-center text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mb-2" />
                <span className="text-xs">Loading module map…</span>
              </div>
            )}
          </TabsContent>

          <TabsContent value="style-violations" className="mt-3" data-testid="audit-tab-style-violations-panel">
            <StyleViolationsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
}
